import { Lock, Send } from "lucide-react";
import { FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Field, RuleList } from "../../components/primitives";
import { useWebsiteResearch } from "../../hooks/useWebsiteResearch";
import { createRun } from "../../lib/api";
import {
  buildRecommendedEditPlan,
  createLocalRun,
  finalizePlannedChanges,
  initialForm,
  resolveAudienceFitSentence,
  resolveOpeningSentence
} from "../../lib/run";
import { loadAuthoritativeConcept, summarizeConcept } from "../../lib/storage";
import type {
  AuthoritativeConcept,
  ChangeItem,
  Mode,
  PersonalizationLevel,
  Purpose,
  RunForm,
  RunStatus,
  WebsiteSuggestion
} from "../../lib/types";
import { EditPlanPanel } from "../editplan/EditPlanPanel";
import { GovernedOptionGroup, type GovernedOption } from "./GovernedOptions";
import { WebsiteResearchPanel } from "./WebsiteResearchPanel";

/**
 * /new — SET UP, then DECIDE. Two phases, one route, because that is what this screen already
 * was logically. It just used to render both at once, on one 7,160px scroll, with the
 * decisions at the BOTTOM.
 *
 * WHAT WAS WRONG (measured live at 390px, not asserted):
 *
 *  1. THE EMPTY FORM WAS 3,633px TALL AND THE ONLY BUTTON WAS AT y=2,992. You scrolled three
 *     and a half screens to find it before typing a character. Once a plan existed the page
 *     was 7,160px and the three decisions you came for sat BELOW the ten option cards you had
 *     already answered. The chrome gives every screen a thumb zone; this one used none of it.
 *     THE FIX: a STICKY ACTION BAR, present from first paint at every scroll position, and a
 *     form that COLLAPSES to one 44px row the moment a plan exists, so the work comes first.
 *
 *  2. RunFlowSteps PAINTED FALSE GREEN TICKS — the exact defect class the mandate calls the
 *     most serious, hiding in a component nobody had audited. `sourceReady` was
 *     `Boolean(activeConcept.title)`, which is TRUE before the operator has done anything, so
 *     an untouched empty form opened with "Source" already ticked green. Worse, `planReady`
 *     was `Boolean(editPlan.length)` — the mere EXISTENCE of proposals — so once a plan
 *     generated, "Edits" showed a green check while the panel directly beneath it read "3
 *     decisions needed". A green tick for outstanding work, on the screen whose entire job is
 *     honest governance. It was also the second, competing progress model on this route.
 *     IT IS DELETED. The only count on this screen is the literal one in the action bar
 *     ("1 of 3 decided"), it is derived from the verdicts themselves, and it can never be a
 *     percentage.
 *
 *  3. THE PROTECTED-FACT PROMISE WAS STATED ELEVEN TIMES ON ONE SCREEN. It is stated once per
 *     option group (three times, where the decision is made) and once, canonically, in "What
 *     the compiler will never do". See GovernedOptions.tsx.
 *
 *  4. DEAD CHROME. The "Document" <select> had exactly ONE option — a control that cannot be
 *     operated, sitting above a field labelled "Document". It is a read-only source line now,
 *     with a link to the one place the source can actually be changed. `form.documentId` is
 *     wired exactly as before, so no capability is lost: there was never a second option.
 *     The run-progress card (which duplicated the button's own busy label, in CAUTION AMBER,
 *     for a completely normal operation) is gone; the busy button and the toast already carry
 *     it, and the toast is the app's ONE aria-live node.
 */

const MODE_LABEL: Record<Mode, string> = {
  draft: "Draft",
  full_diligence: "Full diligence"
};

const PURPOSE_LABEL: Record<Purpose, string> = {
  exploratory_funding: "Exploratory funding",
  formal_submission: "Formal submission",
  program_related_investment: "Program-related investment",
  partnership_proposal: "Partnership proposal"
};

const LEVEL_LABEL: Record<PersonalizationLevel, string> = {
  L0: "L0 · Metadata only",
  L1: "L1 · Light",
  L2: "L2 · Moderate",
  L3: "L3 · Substantive"
};

/**
 * THE LITERAL STATE OF ONE PROPOSED EDIT, IN WORDS. Used by the rail checklist. It is the same
 * vocabulary the row pills already speak ("No decision" / "Accepted" / "Rejected"), it is derived
 * from the verdict itself, and it is never a tick, a percentage or a readiness claim.
 */
const VERDICT_WORD: Record<ChangeItem["verdict"], string> = {
  pending: "No decision",
  approved: "Accepted",
  rejected: "Rejected"
};

/**
 * The rules rail is an expanded RuleList at >=1024 and a closed disclosure below it. That is
 * one component in two containers, decided once, here — NOT a CSS trick: the `open` attribute
 * of a native <details> cannot be set from a media query, and faking it by overriding the UA's
 * hiding of the details content is unreliable across engines. This runs only at >=1024, so it
 * cannot move a pixel on the phone, and the base-layer proof still holds.
 */
function useWideViewport() {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const sync = () => setWide(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);
  return wide;
}

/**
 * THE TOAST MUST NEVER LAND ON THE DOCK.
 *
 * The app's ONE aria-live toast is `position: fixed; bottom: var(--toast-bottom)`, and
 * --toast-bottom clears the tab bar and the home indicator — it knows nothing about a screen
 * that also docks a bar down there. So when the plan generated, "3 recommended edits ready to
 * review" painted straight over the dock at 390/320: it covered the disabled submit's REASON
 * ("3 edits still need a decision…") and half the Create-document button. An overlapping element
 * is banned outright; one that hides a safety gate's reason at the exact moment the operator
 * lands in the deciding phase is worse than banned.
 *
 * The dock's height is not knowable in CSS — it is one row or two depending on `barState`, and it
 * reflows with the reason string's wrap. So the dock MEASURES itself and publishes --dock-h on the
 * root; the screen layer then lifts --toast-bottom by exactly that height for as long as a dock is
 * in the document (`:root:has(.new-dock)`). The toast now docks ABOVE the dock's top hairline.
 *
 * Nothing is shortened, nothing is removed, and the property is torn down on unmount, so no other
 * route ever sees it. At >=1024 no dock renders, the :has() never matches, and the token is the
 * one tokens.css declares.
 */
function useDockHeight(active: boolean) {
  const dockRef = useRef<HTMLDivElement | null>(null);

  const publish = useCallback((height: number) => {
    document.documentElement.style.setProperty("--dock-h", `${Math.ceil(height)}px`);
  }, []);

  useLayoutEffect(() => {
    const dock = dockRef.current;
    const clear = () => document.documentElement.style.removeProperty("--dock-h");
    if (!active || !dock) {
      clear();
      return clear;
    }
    publish(dock.getBoundingClientRect().height);
    // The BORDER box, not the content box: the dock's top hairline and its padding are part of
    // what the toast has to clear.
    const observer = new ResizeObserver(() => publish(dock.getBoundingClientRect().height));
    observer.observe(dock);
    return () => {
      observer.disconnect();
      clear();
    };
  }, [active, publish]);

  return dockRef;
}

const COMPILER_RULES = [
  {
    title: "Protected facts are never rewritten",
    body: "Numbers, dates, currencies, citations, URLs and defined terms pass through untouched, at every personalization level."
  },
  {
    title: "No claim is ever strengthened",
    body: "The compiler will not add evidence, partners, endorsements, results or commitments, and will not raise the certainty of a claim."
  },
  {
    title: "Nothing is applied without a human decision",
    body: "Every proposed edit is accepted or rejected by you before the document is created."
  },
  {
    title: "Export requires a QA pass",
    body: "The package is released only after all six checks return a pass from the backend."
  }
];

export function NewRunPage({
  onRun,
  setToast
}: {
  onRun: (run: RunStatus) => void;
  setToast: (message: string) => void;
}) {
  const [form, setForm] = useState<RunForm>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeConcept] = useState<AuthoritativeConcept>(() => loadAuthoritativeConcept());
  const [editPlan, setEditPlan] = useState<ChangeItem[]>([]);
  const [planGeneratedAt, setPlanGeneratedAt] = useState("");
  const [planDiscarded, setPlanDiscarded] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [websiteTouched, setWebsiteTouched] = useState(false);
  const { research: websiteResearch, refresh: refreshWebsiteResearch } = useWebsiteResearch(form.organization);
  const [researchDismissed, setResearchDismissed] = useState(false);
  const websiteAnchorRef = useRef<HTMLDivElement>(null);
  const researchOpen = websiteResearch.status !== "idle" && !researchDismissed;
  const navigate = useNavigate();
  const wide = useWideViewport();
  const dockRef = useDockHeight(!wide);

  // A fresh lookup is a fresh offer: results for a new organization re-open the popover even if
  // the operator dismissed the last one.
  useEffect(() => {
    setResearchDismissed(false);
  }, [websiteResearch.query]);

  // Dismiss like any popover: Escape, or a click outside the anchor. Selecting a domain closes it
  // at the call site, because the choice is made.
  useEffect(() => {
    if (!researchOpen) return undefined;
    function onPointerDown(event: MouseEvent | TouchEvent) {
      const anchor = websiteAnchorRef.current;
      if (anchor && !anchor.contains(event.target as Node)) setResearchDismissed(true);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setResearchDismissed(true);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [researchOpen]);

  const plannedCount = editPlan.length;
  const pendingCount = editPlan.filter((change) => change.verdict === "pending").length;
  const acceptedCount = editPlan.filter((change) => change.verdict === "approved").length;
  const decidedCount = plannedCount - pendingCount;
  const deciding = plannedCount > 0;

  function discardPlan() {
    // UNCHANGED BEHAVIOUR, NEWLY DISCLOSED. Editing any setting has always destroyed every
    // decision. It did it silently. It now says so, in words, on the panel.
    if (editPlan.length) {
      setEditPlan([]);
      setPlanDiscarded(true);
    }
    setPlanGeneratedAt("");
  }

  function updateField<K extends keyof RunForm>(field: K, value: RunForm[K]) {
    discardPlan();
    setForm((current) => ({ ...current, [field]: value }));
  }

  function updateOrganization(value: string) {
    discardPlan();
    setForm((current) => ({
      ...current,
      organization: value,
      orgWebsite: websiteTouched ? current.orgWebsite : ""
    }));
  }

  function updateWebsite(value: string) {
    setWebsiteTouched(true);
    updateField("orgWebsite", value);
  }

  function applyWebsiteSuggestion(suggestion: WebsiteSuggestion) {
    setWebsiteTouched(false);
    updateField("orgWebsite", suggestion.url);
    setToast(`Selected ${suggestion.domain}`);
  }

  function prepareEditPlan() {
    const plan = buildRecommendedEditPlan(
      {
        ...form,
        documentId: activeConcept.mode === "workspace" ? "workspace-authoritative-concept" : form.documentId,
        sourceConcept: summarizeConcept(activeConcept)
      },
      activeConcept
    );
    setEditPlan(plan);
    setPlanGeneratedAt(new Date().toISOString());
    setPlanDiscarded(false);
    setSetupOpen(false);
    setToast(`${plan.length} recommended edits ready to review`);
  }

  function setPlanVerdict(changeId: string, verdict: ChangeItem["verdict"]) {
    setEditPlan((current) => current.map((change) => (change.id === changeId ? { ...change, verdict } : change)));
  }

  function updatePlanCopy(changeId: string, after: string) {
    setEditPlan((current) =>
      current.map((change) =>
        change.id === changeId
          ? {
              ...change,
              after,
              // SAFETY, PRESERVED VERBATIM: rewriting a REJECTED edit returns it to undecided.
              // The operator has proposed new text; a stale rejection must not silently apply
              // to copy nobody has judged.
              verdict: change.verdict === "rejected" ? "pending" : change.verdict
            }
          : change
      )
    );
  }

  function acceptAllPlanChanges() {
    setEditPlan((current) => current.map((change) => ({ ...change, verdict: "approved" as const })));
  }

  /** Navigation only. Scrolls a plan row into view and focuses its summary. Changes no verdict. */
  function revealRow(summary: HTMLElement | null) {
    if (!summary) return;
    summary.scrollIntoView({ block: "center", behavior: "smooth" });
    summary.focus();
  }

  /** Navigation only. Scrolls the first undecided row into view and focuses it. */
  function goToNextUndecided() {
    revealRow(
      document.querySelector<HTMLElement>('[data-testid="edit-plan-card"][data-verdict="pending"] summary')
    );
  }

  /** Navigation only. The rail checklist jumps to one named edit. */
  function goToChange(index: number) {
    const rows = document.querySelectorAll<HTMLElement>('[data-testid="edit-plan-card"] summary');
    revealRow(rows[index] ?? null);
  }

  async function submitRun(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!editPlan.length) {
      prepareEditPlan();
      return;
    }
    // SAFETY GATE, UNCHANGED: the document may not be created while any recommended edit is
    // undecided. The button is disabled and carries its reason; this is the second lock.
    if (pendingCount) {
      setToast("Accept or reject every recommended edit first");
      return;
    }

    setIsSubmitting(true);
    setToast("Creating document from accepted edits");
    const formWithSource: RunForm = {
      ...form,
      documentId: activeConcept.mode === "workspace" ? "workspace-authoritative-concept" : form.documentId,
      sourceConcept: summarizeConcept(activeConcept),
      plannedChanges: finalizePlannedChanges(editPlan)
    };

    try {
      const run = await createRun(formWithSource);
      onRun(run);
      setToast(`${run.input.organization} draft ready`);
      navigate(`/runs/${run.runId}/studio`);
    } catch {
      const run = await createLocalRun(formWithSource, "blocked");
      onRun(run);
      setToast("AWS did not respond. Local draft created for editing.");
      navigate(`/runs/${run.runId}/studio`);
    } finally {
      setIsSubmitting(false);
    }
  }

  const modeOptions: Array<GovernedOption<Mode>> = [
    {
      value: "draft",
      label: MODE_LABEL.draft,
      consequence: "Produces the personalized copy. The evidence, rendering and reviewer checks do not run.",
      caveat: "Export still requires a full QA pass."
    },
    {
      value: "full_diligence",
      label: MODE_LABEL.full_diligence,
      consequence: "Runs every research, protected-fact, rendering and reviewer check before the package is released."
    }
  ];

  const purposeOptions: Array<GovernedOption<Purpose>> = [
    {
      value: "exploratory_funding",
      label: PURPOSE_LABEL.exploratory_funding,
      consequence: "Frames the close as an exploratory conversation. Makes no commitment and implies no relationship."
    },
    {
      value: "formal_submission",
      label: PURPOSE_LABEL.formal_submission,
      consequence: "Frames the note for a named call, in institutional rather than promotional language."
    },
    {
      value: "program_related_investment",
      label: PURPOSE_LABEL.program_related_investment,
      consequence: "Foregrounds the financing structure and the accountability model."
    },
    {
      value: "partnership_proposal",
      label: PURPOSE_LABEL.partnership_proposal,
      consequence: "Foregrounds shared delivery and governance. States the relationship as proposed, not agreed."
    }
  ];

  const levelOptions: Array<GovernedOption<PersonalizationLevel>> = [
    {
      value: "L0",
      label: LEVEL_LABEL.L0,
      consequence: "Changes the addressee and the document metadata. Not one sentence of the body is touched."
    },
    {
      value: "L1",
      label: LEVEL_LABEL.L1,
      consequence: "Adjusts emphasis and framing sentences. The argument and every claim stay as written."
    },
    {
      value: "L2",
      label: LEVEL_LABEL.L2,
      consequence:
        "Rewrites the opening, the audience-fit passage and the close for this reader. The proposal itself is unchanged."
    },
    {
      value: "L3",
      label: LEVEL_LABEL.L3,
      consequence: "May propose changes to the argument itself.",
      caveat: "Never strengthens the certainty of a claim, and never invents evidence or results."
    }
  ];

  /**
   * THE SUBSTANTIVE-EDIT GATE — a full-measure strip UNDER the four level cards, not a block
   * inside one of them.
   *
   * It lived inside the L3 card, and that is what made the level grid ragged: the tallest, busiest
   * card in the row was the UNSELECTED one, so row-stretch padded the selected L2 card out with
   * ~114px of empty blue to match it, and the gate itself read as a card inside a card. Sizing the
   * cards to their content instead only traded that for a ragged row.
   *
   * The gate is not weakened by moving. It is the same checkbox on the same field, still required
   * before L3 may propose an argument change, still independently settable at any level (it always
   * was — changing it still discards the plan, exactly as before), and it now states that
   * requirement at a full reading measure directly beneath the card it governs. It carries the
   * PROTECTED warm family (lock glyph, --surface-locked ground) the moment L3 is the chosen level,
   * because that is the moment it is standing between the operator and an argument change.
   */
  const substantiveGate = (
    <div className="option-gate-strip" data-required={form.personalizationLevel === "L3" ? "true" : "false"}>
      <label className="check-row">
        <input
          type="checkbox"
          checked={form.allowSubstantive}
          onChange={(event) => updateField("allowSubstantive", event.target.checked)}
        />
        <span>Allow substantive edits</span>
      </label>
      <p className="option-gate-note">
        <Lock aria-hidden="true" />
        <span>Required before L3 can propose an argument change. Each one is flagged and needs its own approval.</span>
      </p>
    </div>
  );

  const sourceLine =
    activeConcept.mode === "workspace"
      ? `Workspace source · ${activeConcept.filename}`
      : `${activeConcept.title} · packaged canonical`;

  /**
   * THE DOCUMENT, ON THE SCREEN THAT SETS UP THE DOCUMENT.
   *
   * /new used to contain no artefact at all: an operator chose a personalization LEVEL for a
   * concept note they could not see. The doc card is the source, rendered as paper — serif,
   * a real cover line, a real sentence of its real opening.
   *
   * EVERY STRING HERE IS READ FROM THE ACTIVE CONCEPT OR FROM THE FORM. Nothing is invented:
   * the excerpt is the SAME sentence buildRecommendedEditPlan() takes as the opening `before`,
   * resolved by the same helper against the same source text, so the card cannot show copy the
   * compiler is not actually working from. There is no page count, because no page count exists
   * in the data.
   *
   * HONESTY: the card renders SOURCE text only. It never renders a proposal as if it were
   * applied. Its caption is either "not yet personalized" or the literal, derived count.
   */
  const docKicker =
    activeConcept.mode === "workspace"
      ? `Workspace source · ${activeConcept.filename}`
      : `Canonical source · ${activeConcept.fileType}`;
  /**
   * ONE CALL PATH, SHARED WITH THE PLAN BUILDER. This is literally the sentence
   * buildRecommendedEditPlan() takes as the `before` of edit 1 — same helper, same source — so the
   * page and the diff two panels away can never disagree. It used to fall through to
   * `textExcerpt`, which is the concept's METADATA BLURB, so the artefact whose whole job is to
   * make the document read as a document was showing a caption. If the helper ever resolves to
   * nothing, no excerpt renders at all.
   */
  const docExcerpt = resolveOpeningSentence(activeConcept);
  /**
   * THE SECOND PARAGRAPH OF THE PAGE, AND IT IS REAL — AND IT NOW ACTUALLY RENDERS.
   *
   * It used to call findSourceSentence(…, "catalytic financing") directly. With the PACKAGED
   * canonical source there is no `text` at all, so that probe returned "" and the second paragraph
   * rendered NOTHING at every width — which is why the sheet was a title, one sentence and ~180px
   * of empty cream. Meanwhile the diff two panels down happily printed the plan's declared
   * fallback for the same sentence: two call paths, one source, two different answers.
   *
   * It now goes through resolveAudienceFitSentence(), the SAME helper buildRecommendedEditPlan()
   * uses for the `before` of the audience-fit edit. No filler was written: this is literally the
   * text the compiler is working from, and if a workspace source is loaded the probe wins and both
   * surfaces move together.
   */
  const docExcerptTwo = resolveAudienceFitSentence(activeConcept);
  const addresseeParts = [form.organization.trim(), form.recipientName.trim(), form.recipientRole.trim()].filter(
    Boolean
  );
  const docCard = (
    <article className="doc-card" aria-label="Source document">
      {/* THE EYEBROW IS PART OF THE PAGE'S HEAD, NOT OF ITS TITLE BLOCK — and separating the two is
          what fixes the sheet's one remaining alignment defect. The cover was a single block with
          `margin-top: auto`, so when the paper GREW to fill the rail (the deciding phase) the whole
          block was pushed down together and the kicker — the paper's first line of type — floated
          125px (1440) / 180px (1920) below the sheet's own top padding. The card therefore changed
          its apparent internal padding between the setup and deciding phases, and the paper's first
          line stopped sharing a baseline with the first line of the settings card in the column
          beside it.
          The kicker is now top-anchored, exactly as it is in setup, and ONLY the title and its live
          addressee sit in the growth space above the bottom-anchored folio. Cross-column first-line
          alignment holds in both phases, and the title-page composition — eyebrow at the head, title
          set into the page, folio on the bottom margin — is the stronger of the two anyway. */}
      <header className="doc-cover">
        <p className="doc-kicker">{docKicker}</p>
        <div className="doc-cover-title">
          <h2 className="doc-title">{activeConcept.title}</h2>
          <p className="doc-addressee" data-empty={addresseeParts.length ? "false" : "true"}>
            {addresseeParts.length ? `Prepared for ${addresseeParts.join(" · ")}` : "Prepared for —"}
          </p>
        </div>
      </header>
      {docExcerpt ? (
        <div className="doc-excerpt">
          <p>{docExcerpt}</p>
          {docExcerptTwo ? <p className="doc-excerpt-2">{docExcerptTwo}</p> : null}
        </div>
      ) : null}
      <p className="doc-state">
        {deciding
          ? `${plannedCount} proposed edit${plannedCount === 1 ? "" : "s"} · ${decidedCount} decided`
          : "Source text, not yet personalized"}
      </p>
    </article>
  );

  /** The whole setup form. It is the page in phase 1, and one 44px disclosure in phase 2. */
  const setup = (
    <div className="setup-body">
      <section className="setup-section">
        <h3 className="setup-section-title">
          <span className="setup-step" aria-hidden="true">
            1
          </span>
          Recipient
        </h3>
        <div className="field-row">
          <Field label="Organization" help="We look for the official website as you type.">
            <input
              required
              aria-label="Organization"
              value={form.organization}
              onChange={(event) => updateOrganization(event.target.value)}
              onInvalid={() => setSetupOpen(true)}
              placeholder="e.g. Wellcome Trust"
            />
          </Field>
          {/* The suggestion list is anchored to the field it fills in, and floats over the
              content. See .website-anchor / .website-research in the screen layer. */}
          <div className="website-anchor" ref={websiteAnchorRef}>
            <Field label="Website">
              <input
                required
                type="url"
                aria-label="Website"
                value={form.orgWebsite}
                onChange={(event) => updateWebsite(event.target.value)}
                onFocus={() => setResearchDismissed(false)}
                onInvalid={() => setSetupOpen(true)}
                placeholder="https://example.org"
                role="combobox"
                aria-expanded={researchOpen}
                aria-controls="website-research-popover"
                aria-autocomplete="list"
              />
            </Field>
            {researchOpen ? (
              <WebsiteResearchPanel
                id="website-research-popover"
                research={websiteResearch}
                selectedUrl={form.orgWebsite}
                onSelect={(suggestion) => {
                  applyWebsiteSuggestion(suggestion);
                  setResearchDismissed(true);
                }}
                onRefresh={refreshWebsiteResearch}
              />
            ) : null}
          </div>
        </div>

        <div className="field-row">
          <Field label="Recipient name">
            <input
              aria-label="Recipient name"
              value={form.recipientName}
              onChange={(event) => updateField("recipientName", event.target.value)}
              placeholder="If known"
            />
          </Field>
          <Field label="Role">
            <input
              required
              aria-label="Role"
              value={form.recipientRole}
              onChange={(event) => updateField("recipientRole", event.target.value)}
              onInvalid={() => setSetupOpen(true)}
              placeholder="Head of Digital Health"
            />
          </Field>
        </div>
      </section>

      <section className="setup-section">
        <h3 className="setup-section-title">
          <span className="setup-step" aria-hidden="true">
            2
          </span>
          Purpose
        </h3>
        {/* The fieldset keeps its accessible name — the legend is only hidden from SIGHT,
            because the section head one line above says the identical word. */}
        <GovernedOptionGroup
          label="Purpose"
          hideLegend
          help="What this document is being sent to do. It sets the tone of the close, not the facts."
          promise="No purpose adds a partner, an endorsement, a result or a commitment."
          columns={2}
          value={form.purpose}
          options={purposeOptions}
          onChange={(value) => updateField("purpose", value)}
        />
      </section>

      <section className="setup-section">
        <h3 className="setup-section-title">
          <span className="setup-step" aria-hidden="true">
            3
          </span>
          How far the compiler may go
        </h3>
        <GovernedOptionGroup
          label="Run mode"
          help="Which checks run before the package can be exported."
          promise="Neither mode changes protected facts."
          columns={2}
          value={form.mode}
          options={modeOptions}
          onChange={(value) => updateField("mode", value)}
        />
        <GovernedOptionGroup
          label="Personalization level"
          help="How much of the document the compiler may rewrite."
          promise="At every level, protected numbers, dates, currencies, citations, URLs and defined terms pass through unchanged."
          gate={substantiveGate}
          columns={2}
          value={form.personalizationLevel}
          options={levelOptions}
          onChange={(value) => updateField("personalizationLevel", value)}
        />
      </section>

      <details className="setup-disclosure" data-testid="optional-context">
        <summary className="setup-disclosure-summary">Optional context</summary>
        <div className="setup-disclosure-body">
          <Field label="Funding call">
            <input
              aria-label="Funding call"
              value={form.fundingCall}
              onChange={(event) => updateField("fundingCall", event.target.value)}
              placeholder="Programme or vehicle"
            />
          </Field>
          <Field label="Relationship context">
            <input
              aria-label="Relationship context"
              value={form.relationshipContext}
              onChange={(event) => updateField("relationshipContext", event.target.value)}
              placeholder="Warm intro, meeting, or prior context"
            />
          </Field>
          <Field label="Operator notes">
            <textarea
              aria-label="Operator notes"
              value={form.notes}
              onChange={(event) => updateField("notes", event.target.value)}
              placeholder="Angle, sensitivity, or wording preference"
              rows={3}
            />
          </Field>
          {/* Replaces a <select> that had exactly one <option>. */}
          <div className="source-row">
            <span className="source-row-label">Source</span>
            <span className="source-row-value">{sourceLine}</span>
            <Link className="source-row-link" to="/settings">
              Change in Settings
            </Link>
          </div>
        </div>
      </details>
    </div>
  );

  const submitLabel = isSubmitting
    ? "Creating document"
    : !deciding
      ? "Review recommended edits"
      : pendingCount
        ? "Create document"
        : acceptedCount
          ? `Create document from ${acceptedCount} accepted edit${acceptedCount === 1 ? "" : "s"}`
          : "Create document with no text edits";

  const barState = isSubmitting ? "submitting" : !deciding ? "setup" : pendingCount ? "blocked" : "deciding";

  /**
   * WHAT THE DECISION PANEL SAYS WHILE THE FORM IS STILL BEING FILLED.
   *
   * The setup-phase action block used to render BARE — no panel, no padding, no ground — so at
   * 1440 the primary was a naked pill sitting in an empty column, ~1,700px above the end of the
   * form it submits, and the same slot acquired a card two seconds later once a plan existed. A
   * control with no container, in a column with nothing else in it, is a floating element, and a
   * screen that contradicts itself between its own phases is not a designed screen.
   *
   * So the block is ALWAYS a panel, and in the setup phase the panel carries the content it
   * always had to say: what the compiler is about to do, and which required inputs are still
   * missing before it can. Every string here is DERIVED from the form — the three inputs the
   * <input required> attributes already gate on — so it can never claim readiness the form does
   * not have, and it never ticks anything: an empty input reads "Needed", a filled one echoes the
   * operator's own words back. It also puts the missing-field list next to the button that will
   * be refused, instead of leaving the browser's validation bubble to fire 1,700px away.
   */
  const requiredInputs = [
    { label: "Organization", value: form.organization.trim() },
    { label: "Website", value: form.orgWebsite.trim() },
    { label: "Role", value: form.recipientRole.trim() }
  ];

  /**
   * THE ACTION BLOCK — ONE component, TWO containers, decided once, here.
   *
   * THE BUG IT KILLS: this used to be `.new-action-bar`, a ROUNDED, ELEVATED, OPAQUE CARD with
   * `position: sticky; bottom: …`. On a form 3,633px tall (empty) / 7,160px (plan) that means it
   * hovered at the bottom of the viewport, ON TOP of the form's own cards, for the entire scroll
   * — squarely over the Draft / Full-diligence run-mode cards, and clipping the "2. Audience fit"
   * heading. A card that floats over other cards does not read as chrome; it reads as a card that
   * has come loose.
   *
   * THE FIX IS STRUCTURAL, NOT COSMETIC:
   *   < 1024  it is `.new-dock`: full-bleed, SQUARE, one hairline on top, frosted. Chrome, the way
   *           iOS does a toolbar. Content sliding under a dock is then intentional, and
   *           `.new-main`'s bottom padding lets the last row scroll clear of it regardless.
   *   >= 1024 IT IS NOT RENDERED AT ALL. It moves into the rail, where the artefact and the
   *           guarantees already live. There is then NO sticky element in the form column, so
   *           overlap is structurally impossible rather than merely tuned away.
   *
   * The form is now the grid, so this submit needs no `form=` attribute in either container.
   * Behaviour, payload, copy, counts and every gate are byte-for-byte what they were: the count
   * is literal and derived from the verdicts, never a percentage, never a bar; the primary stays
   * disabled with its full plain-language reason while any edit is undecided.
   */
  const actionBlock = (
    <div
      ref={wide ? undefined : dockRef}
      className={wide ? "rail-actions" : "new-dock"}
      data-testid="new-action-bar"
      data-state={barState}
    >
      {/* THE SETUP PHASE'S PANEL HAS A HEAD, BECAUSE IT HAS SOMETHING TO EXPLAIN: nothing has been
          proposed yet, so the panel says what is about to happen and which required inputs are
          still missing. The DECIDING phase does not, and that is not a saving — it is the right
          shape. Its head would be a label over a list that names itself: the literal count, then
          every proposed edit by name with its verdict. A title above that is a word about words,
          and it would cost the column the height the plan itself needs. The dock has no head at any
          time — a phone toolbar is controls, and the form it belongs to is one thumb away. */}
      {wide && !deciding ? (
        <header className="rail-actions-head">
          <h2 className="rail-actions-title">What happens next</h2>
          <p className="rail-actions-note">
            The compiler reads the source and proposes edits for this reader. You accept or reject each one
            before the document is created.
          </p>
        </header>
      ) : null}

      {deciding ? (
        <p className="action-bar-count" data-testid="plan-progress">
          {decidedCount === 0
            ? `No decisions yet — ${plannedCount} edit${plannedCount === 1 ? "" : "s"} to review`
            : `${decidedCount} of ${plannedCount} decided`}
        </p>
      ) : null}

      {/* THE REQUIRED INPUTS, BESIDE THE BUTTON THAT NEEDS THEM. Derived from the form; never a
          tick, never a percentage, never a claim of readiness the fields do not carry. */}
      {wide && !deciding ? (
        <dl className="rail-inputs">
          {requiredInputs.map((input) => (
            <div className="rail-input-row" data-filled={input.value ? "true" : "false"} key={input.label}>
              <dt className="rail-input-label">{input.label}</dt>
              <dd className="rail-input-value">{input.value || "Needed"}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {/* THE CHECKLIST — THE DEAD COLUMN'S REAL FIX.
          The rail used to hold a 257px decision panel against a 2,004px form, so two-thirds of the
          right column was void grey at every working scroll offset. Filling it with decoration was
          never an option; filling it with the WORK is: every proposed edit, by name, carrying its
          own live verdict in words, and each one a jump to its row.

          It is navigation ONLY — it sets no verdict, and it is derived entirely from `editPlan`, so
          it cannot disagree with the rows it points at. The words are the literal ones ("No
          decision" / "Accepted" / "Rejected"); there is no tick, no bar and no percentage. It turns
          "No decisions yet — 3 edits to review" from a statement into a table of contents. */}
      {wide && deciding ? (
        <ul className="rail-plan">
          {editPlan.map((change, index) => (
            <li className="rail-plan-item" key={change.id}>
              <button
                type="button"
                className="rail-plan-row"
                data-verdict={change.verdict}
                onClick={() => goToChange(index)}
              >
                <span className="rail-plan-index" aria-hidden="true">
                  {index + 1}
                </span>
                <span className="rail-plan-copy">
                  <span className="rail-plan-title">{change.title}</span>
                  <span className="rail-plan-state">{VERDICT_WORD[change.verdict]}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="action-bar-controls">
        {pendingCount ? (
          /* SECONDARY, NOT GHOST. It sits next to a prominent DISABLED primary and it is the one
             affordance that unblocks it — as a bare ghost it had no resting chrome at all and did
             not read as a control. It now carries a hairline and a ground. */
          <Button variant="secondary" size="sm" data-testid="edit-plan-next" onClick={goToNextUndecided}>
            Next undecided
          </Button>
        ) : null}

        <Button
          variant="primary"
          size="lg"
          block
          type="submit"
          data-testid="new-run-submit"
          busy={isSubmitting}
          disabled={isSubmitting || pendingCount > 0}
          reason={
            pendingCount
              ? `${pendingCount} edit${pendingCount === 1 ? "" : "s"} still need${pendingCount === 1 ? "s" : ""} a decision. The document cannot be created until every proposal is accepted or rejected.`
              : undefined
          }
        >
          {submitLabel}
          {isSubmitting ? null : <Send aria-hidden="true" />}
        </Button>
      </div>
    </div>
  );

  return (
    <form className="page-grid new-run-page" data-testid="new-run-page" onSubmit={submitRun}>
      {/* THE HEAD SPANS BOTH COLUMNS, so at >=1024 the form panel and the rail's first card
          share ONE top edge. The lede used to sit inside the left column, which started the
          form 72px below the rail. */}
      <header className="new-head">
        <h2 className="new-head-title">Personalize the concept note</h2>
        {/* THE LEAD. Said ONCE, here, and nowhere else on the screen. */}
        {deciding ? null : (
          <p className="new-lead">
            Personalize the ATLAS DPI Concept Note for one organization. You decide on every proposed edit before
            the document is created.
          </p>
        )}
      </header>

      <div className="new-main">
        {/* The document leads the phone, and lives in the rail from 1024 up. One component,
            never both at once. */}
        {wide ? null : docCard}

        {deciding ? (
          <section className="panel setup-panel">
            <details
              className="setup-collapsed"
              data-testid="setup-summary"
              open={setupOpen}
              onToggle={(event) => {
                const el = event.target as HTMLDetailsElement | null;
                if (el) setSetupOpen(el.open);
              }}
            >
              <summary className="setup-collapsed-summary">
                <span className="setup-collapsed-copy">
                  <span className="setup-collapsed-line">
                    {form.organization || "Organization"} · {MODE_LABEL[form.mode]} ·{" "}
                    {LEVEL_LABEL[form.personalizationLevel]} · {PURPOSE_LABEL[form.purpose]}
                  </span>
                  <span className="setup-collapsed-note">
                    Changing any setting discards these recommendations and rebuilds them.
                  </span>
                </span>
                <span className="setup-collapsed-action">Change</span>
              </summary>
              {setup}
            </details>
          </section>
        ) : (
          <section className="panel setup-panel">{setup}</section>
        )}

        <EditPlanPanel
          changes={editPlan}
          generatedAt={planGeneratedAt}
          pendingCount={pendingCount}
          discarded={planDiscarded}
          onAcceptAll={acceptAllPlanChanges}
          onVerdict={setPlanVerdict}
          onUpdateCopy={updatePlanCopy}
        />

        {/* THE GUARANTEES CLOSE THE FORM COLUMN, AT EVERY WIDTH.
            They used to live in the rail at >=1024, and that was the cause of the second dead
            column: once every edit was decided the form column collapsed to ~472px while the rail
            still ran ~808px, so the left column simply STOPPED 336px above the right one.

            The fix is not filler — it is putting the standing rules where they balance the grid
            AND read better: a full-measure panel at the end of the form, two-up at >=1024. The
            rail is then exactly [document] -> [decision], which is shorter than the form column in
            every state, so the columns can no longer end in different places. Nothing is hidden:
            below 1024 the rules stay a disclosure (a phone has no room to print four standing
            rules in full), and at >=1024 they are simply VISIBLE rather than disclosed — strictly
            more of the promise, never less. */}
        {wide ? (
          <section className="panel rules-panel">
            <h2 className="rules-panel-title">What the compiler will never do</h2>
            <RuleList label="Compiler rules" items={COMPILER_RULES} />
          </section>
        ) : (
          <details className="rules-disclosure">
            <summary className="rules-disclosure-summary">What the compiler will never do</summary>
            <div className="rules-disclosure-body">
              <RuleList label="Compiler rules" items={COMPILER_RULES} />
            </div>
          </details>
        )}

        {/* THE THUMB ZONE, below 1024 only. A square, full-bleed, hairlined, frosted DOCK —
            not a rounded elevated card floating over the form. */}
        {wide ? null : actionBlock}
      </div>

      {/* THE RAIL, >=1024: [ARTEFACT] -> [DECISION], AND BOTH OF THEM STAY.
          Round 6 made the DECISION panel sticky and left the document to scroll away above it. The
          result was the dead column the owner banned: past the hero, the right column held one
          257px panel against a 2,004px form — ~1,178px of void grey at 1920, at every working
          scroll offset.

          The document does not scroll away any more. The rail is ONE sticky box (never two stacked
          sticky siblings — that is what makes siblings slide under each other) holding the PAGE and
          the DECISION ON IT, and it travels with the operator for the whole length of the form. You
          can always see the artefact you are editing, the count, the checklist of every proposed
          edit and the primary action, together, at any scroll offset. Nothing in the column is
          decorative, nothing is a placeholder, and nothing is empty in either phase. */}
      {wide ? (
        <aside className="new-rail">
          <div className="rail-inner">
            {docCard}

            {/* THE DECISION IS ALWAYS A PANEL — IN BOTH PHASES.
                It used to render BARE in the setup phase (a button with no container, in a column
                with nothing else in it) and acquire a card only once a plan existed: a floating
                control, and a screen that disagreed with itself between its own phases. It is one
                surface now, with real content in both states — what happens next and what is still
                missing, then the literal count, the checklist of every edit, and the gate's
                reason. */}
            <section className="panel rail-panel">{actionBlock}</section>
          </div>
        </aside>
      ) : null}
    </form>
  );
}
